import React, { useState, useEffect } from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { HierarchySelectorProps, EduDomain, EduSubject, EduKnowledgePoint } from '../../types/learning';
import { API_CONFIG } from '../../config/api';
import { supabase } from '../../lib/supabase';
import toast from 'react-hot-toast';

const HierarchySelector: React.FC<HierarchySelectorProps> = ({
  value = {},
  onChange,
  required = {},
  disabled = false,
  className = ''
}) => {
  const [domains, setDomains] = useState<EduDomain[]>([]);
  const [subjects, setSubjects] = useState<EduSubject[]>([]);
  const [knowledgePoints, setKnowledgePoints] = useState<EduKnowledgePoint[]>([]);
  const [loading, setLoading] = useState({
    domains: false,
    subjects: false,
    knowledgePoints: false
  });

  // 加载域列表
  useEffect(() => {
    fetchDomains();
  }, []);

  // 当域改变时加载学科
  useEffect(() => {
    if (value.domain_id) {
      fetchSubjects(value.domain_id);
    } else {
      setSubjects([]);
      setKnowledgePoints([]);
    }
  }, [value.domain_id]);

  // 当学科改变时加载知识点
  useEffect(() => {
    if (value.subject_id) {
      fetchKnowledgePoints(value.subject_id);
    } else {
      setKnowledgePoints([]);
    }
  }, [value.subject_id]);

  const fetchDomains = async () => {
    try {
      setLoading(prev => ({ ...prev, domains: true }));
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-domains-admin-manage?action=list&limit=100`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch domains');

      const result = await response.json();
      if (result.success) {
        setDomains(result.data.data || []);
      }
    } catch (error) {
      console.error('获取域列表失败:', error);
      toast.error('获取域列表失败');
    } finally {
      setLoading(prev => ({ ...prev, domains: false }));
    }
  };

  const fetchSubjects = async (domainId: string) => {
    try {
      setLoading(prev => ({ ...prev, subjects: true }));
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-subjects-admin-manage?action=list&domain_id=${domainId}&limit=100`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch subjects');

      const result = await response.json();
      if (result.success) {
        setSubjects(result.data.data || []);
      }
    } catch (error) {
      console.error('获取学科列表失败:', error);
      toast.error('获取学科列表失败');
    } finally {
      setLoading(prev => ({ ...prev, subjects: false }));
    }
  };

  const fetchKnowledgePoints = async (subjectId: string) => {
    try {
      setLoading(prev => ({ ...prev, knowledgePoints: true }));
      const response = await fetch(`${API_CONFIG.FUNCTIONS_URL}/edu-knowledge-admin-manage?action=list&subject_id=${subjectId}&limit=100`, {
        headers: {
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to fetch knowledge points');

      const result = await response.json();
      if (result.success) {
        setKnowledgePoints(result.data.data || []);
      }
    } catch (error) {
      console.error('获取知识点列表失败:', error);
      toast.error('获取知识点列表失败');
    } finally {
      setLoading(prev => ({ ...prev, knowledgePoints: false }));
    }
  };

  const handleDomainChange = (domainId: string) => {
    onChange({
      domain_id: domainId || undefined,
      subject_id: undefined,
      point_id: undefined
    });
  };

  const handleSubjectChange = (subjectId: string) => {
    onChange({
      ...value,
      subject_id: subjectId || undefined,
      point_id: undefined
    });
  };

  const handleKnowledgePointChange = (pointId: string) => {
    onChange({
      ...value,
      point_id: pointId || undefined
    });
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* 域选择 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          领域 {required.domain && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <select
            value={value.domain_id || ''}
            onChange={(e) => handleDomainChange(e.target.value)}
            disabled={disabled || loading.domains}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed appearance-none"
            required={required.domain}
          >
            <option value="">请选择领域</option>
            {domains.map((domain) => (
              <option key={domain.id} value={domain.id}>
                {domain.name}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            {loading.domains ? (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* 学科选择 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          学科 {required.subject && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <select
            value={value.subject_id || ''}
            onChange={(e) => handleSubjectChange(e.target.value)}
            disabled={disabled || loading.subjects || !value.domain_id}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed appearance-none"
            required={required.subject}
          >
            <option value="">请选择学科</option>
            {subjects.map((subject) => (
              <option key={subject.id} value={subject.id}>
                {subject.name}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            {loading.subjects ? (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* 知识点选择 */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          知识点 {required.knowledge_point && <span className="text-red-500">*</span>}
        </label>
        <div className="relative">
          <select
            value={value.point_id || ''}
            onChange={(e) => handleKnowledgePointChange(e.target.value)}
            disabled={disabled || loading.knowledgePoints || !value.subject_id}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed appearance-none"
            required={required.knowledge_point}
          >
            <option value="">请选择知识点</option>
            {knowledgePoints.map((point) => (
              <option key={point.id} value={point.id}>
                {point.name}
              </option>
            ))}
          </select>
          <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
            {loading.knowledgePoints ? (
              <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
            ) : (
              <ChevronDown className="w-4 h-4 text-gray-400" />
            )}
          </div>
        </div>
      </div>

      {/* 层级路径显示 */}
      {(value.domain_id || value.subject_id || value.point_id) && (
        <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">
          <span className="font-medium">当前路径：</span>
          {value.domain_id && (
            <span className="ml-1">
              {domains.find(d => d.id === value.domain_id)?.name}
            </span>
          )}
          {value.subject_id && (
            <span className="ml-1">
              → {subjects.find(s => s.id === value.subject_id)?.name}
            </span>
          )}
          {value.point_id && (
            <span className="ml-1">
              → {knowledgePoints.find(p => p.id === value.point_id)?.name}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default HierarchySelector;